import React, { useState, useEffect } from 'react';
import { ref, onValue, update, remove, push } from "firebase/database";
import { useNavigate } from 'react-router-dom';
import { db, auth } from './firebase';
import { inventoryData } from './data'; 

function TeacherDashboard() {
  const [requests, setRequests] = useState([]);
  const [reagents, setReagents] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSecretCode, setCurrentSecretCode] = useState('');
  const [newSecretCode, setNewSecretCode] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);

  const [editingReagent, setEditingReagent] = useState({
    id: '', KORNAME: '', IUPACNAME: '', TRINAME: '',
    MOL_FORMULA: '', CAS_NO: '', LOCATION: '', COUNT: '',
    NFPA_R: '', NFPA_Y: '', NFPA_B: '', NFPA_W: '', MOL_MASS: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    const unsubApps = onValue(ref(db, 'applications'), (snapshot) => {
      const data = snapshot.val();
      setRequests(data ? Object.keys(data).map(key => ({ id: key, ...data[key] })).reverse() : []);
    });
    const unsubReagents = onValue(ref(db, 'reagents'), (snapshot) => {
      const data = snapshot.val();
      setReagents(data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : []);
    });
    const unsubSettings = onValue(ref(db, 'settings/teacherCode'), (snapshot) => {
      setCurrentSecretCode(snapshot.val() || 'chem1004');
    });
    return () => { unsubApps(); unsubReagents(); unsubSettings(); };
  }, []);

  const handleUpdateSecretCode = () => {
    if (!newSecretCode) return alert("새 비밀번호를 입력해주세요!");
    if (window.confirm(`선생님 가입 비밀번호를 '${newSecretCode}'(으)로 변경하시겠습니까?`)) {
      update(ref(db, 'settings'), { teacherCode: newSecretCode })
        .then(() => {
          alert("비밀번호가 성공적으로 변경되었습니다! 🔐");
          setNewSecretCode('');
        });
    }
  };

  const pendingRequests = requests.filter(req => req.status === '대기중');
  const processedRequests = requests.filter(req => req.status !== '대기중');

  const pendingBatches = pendingRequests.reduce((acc, req) => {
    const key = req.batchId || req.id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(req);
    return acc;
  }, {});

  const processedBatches = processedRequests.reduce((acc, req) => {
    const key = req.batchId || req.id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(req);
    return acc;
  }, {});

  const handleBatchStatusUpdate = (batchItems, newStatus) => {
    const user = auth.currentUser;
    const processorName = user ? (user.displayName || user.email) : '알 수 없는 관리자';
    const promises = batchItems.map(item =>
      update(ref(db, `applications/${item.id}`), {
        status: newStatus,
        processor: processorName
      })
    );
    Promise.all(promises).catch(err => alert("처리 실패: " + err.message));
  };

  const filteredReagents = reagents.filter(item => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    return (
      (item.KORNAME && item.KORNAME.includes(lowerQuery)) ||
      (item.MOL_FORMULA && item.MOL_FORMULA.toLowerCase().includes(lowerQuery)) ||
      (item.IUPACNAME && item.IUPACNAME.toLowerCase().includes(lowerQuery)) ||
      (item.TRINAME && item.TRINAME.includes(lowerQuery))
    );
  });

  const handleDeleteRequest = (id) => {
    if (window.confirm("내역을 완전히 삭제하시겠습니까?")) remove(ref(db, `applications/${id}`));
  };

  // 🌟 [추가된 부분] 데이터 정제 필터: Firebase가 싫어하는 NaN, null, undefined를 모두 빈칸으로 바꿔줍니다!
  const sanitize = (item) => {
    const cleaned = {};
    for (let key in item) {
      let val = item[key];
      if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
        cleaned[key] = ""; 
      } else {
        cleaned[key] = val;
      }
    }
    return cleaned;
  };

  // ✅ 초기 데이터 업로드 
  const handleMigrateData = () => {
    if (reagents.length > 0) return alert("이미 DB에 시약 정보가 있습니다!\n'설정' 탭에서 전체 교체를 이용하세요.");
    if (window.confirm(`JSON 파일의 시약 데이터 ${inventoryData.length}개를 Firebase에 업로드하시겠습니까?`)) {
      const chunkSize = 10;
      const uploadChunks = async () => {
        for (let i = 0; i < inventoryData.length; i += chunkSize) {
          // ✨ 데이터를 올리기 전에 sanitize 필터망을 통과시킵니다.
          const chunk = inventoryData.slice(i, i + chunkSize).map(sanitize);
          await Promise.all(chunk.map(item => push(ref(db, 'reagents'), item)));
        }
        alert(`✅ 업로드 완료! 총 ${inventoryData.length}개의 시약이 등록되었습니다.`);
      };
      uploadChunks().catch(err => alert("오류: " + err.message));
    }
  };

  // ✅ 전체 교체
  const handleReplaceAllReagents = async () => {
    if (!window.confirm(`⚠️ 주의!\n현재 DB의 시약 ${reagents.length}개를 모두 삭제하고\n새 데이터 ${inventoryData.length}개로 교체합니다.\n\n이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?`)) return;
    setIsReplacing(true);
    try {
      await Promise.all(reagents.map(item => remove(ref(db, `reagents/${item.id}`))));
      const chunkSize = 10;
      for (let i = 0; i < inventoryData.length; i += chunkSize) {
        // ✨ 데이터를 올리기 전에 sanitize 필터망을 통과시킵니다.
        const chunk = inventoryData.slice(i, i + chunkSize).map(sanitize);
        await Promise.all(chunk.map(item => push(ref(db, 'reagents'), item)));
      }
      alert(`✅ 교체 완료! 새 시약 데이터 ${inventoryData.length}개가 업로드되었습니다.`);
    } catch (err) {
      alert("❌ 오류 발생: " + err.message);
    } finally {
      setIsReplacing(false);
    }
  };

  const handleSaveReagent = () => {
    if (!editingReagent.KORNAME) return alert("시약의 한글명은 필수입니다!");
    if (editingReagent.id) {
      const { id, ...dataToUpdate } = editingReagent;
      update(ref(db, `reagents/${id}`), sanitize(dataToUpdate)).then(() => {
        alert("수정 완료!");
        setIsEditModalOpen(false);
      });
    } else {
      const { id, ...newData } = editingReagent;
      push(ref(db, 'reagents'), sanitize(newData)).then(() => {
        alert("추가 완료!");
        setIsEditModalOpen(false);
      });
    }
  };

  const handleDeleteReagent = (id) => {
    if (window.confirm("이 시약을 삭제하시겠습니까?")) remove(ref(db, `reagents/${id}`));
  };

  const openReagentModal = (reagent = null) => {
    if (reagent) {
      setEditingReagent({
        id: reagent.id,
        KORNAME: reagent.KORNAME || '',
        IUPACNAME: reagent.IUPACNAME || '',
        TRINAME: reagent.TRINAME || '',
        MOL_FORMULA: reagent.MOL_FORMULA || '',
        MOL_MASS: reagent.MOL_MASS || '',
        CAS_NO: reagent.CAS_NO || '',
        NFPA_R: reagent.NFPA_R || '',
        NFPA_Y: reagent.NFPA_Y || '',
        NFPA_B: reagent.NFPA_B || '',
        NFPA_W: reagent.NFPA_W || '',
        LOCATION: reagent.LOCATION || '',
        COUNT: reagent.COUNT || '',
      });
    } else {
      setEditingReagent({
        id: '', KORNAME: '', IUPACNAME: '', TRINAME: '',
        MOL_FORMULA: '', MOL_MASS: '', CAS_NO: '',
        NFPA_R: '', NFPA_Y: '', NFPA_B: '', NFPA_W: '',
        LOCATION: '', COUNT: ''
      });
    }
    setIsEditModalOpen(true);
  };

  const tabStyle = (tab) => ({
    padding: '10px 20px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '0.95em',
    borderBottom: activeTab === tab ? '3px solid #3498db' : '3px solid transparent',
    backgroundColor: 'transparent',
    color: activeTab === tab ? '#3498db' : '#7f8c8d',
    transition: '0.2s',
  });

  return (
    <div className="container" style={{ padding: '30px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#2c3e50' }}>👩‍🏫 선생님 대시보드</h1>
          <p style={{ margin: '5px 0 0 0', color: '#7f8c8d', fontSize: '0.9em' }}>
            시약 신청 승인 및 재고 관리
          </p>
        </div>
        <button
          onClick={() => { if (window.confirm("로그아웃하시겠습니까?")) navigate('/'); }}
          style={{ padding: '8px 16px', backgroundColor: '#ecf0f1', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
        >
          🚪 로그아웃
        </button>
      </div>

      {/* 탭 메뉴 */}
      <div style={{ borderBottom: '1px solid #eee', marginBottom: '25px', display: 'flex', gap: '5px' }}>
        <button style={tabStyle('pending')} onClick={() => setActiveTab('pending')}>
          ⏳ 대기중 신청 <span style={{ backgroundColor: '#e74c3c', color: 'white', borderRadius: '10px', padding: '1px 8px', fontSize: '0.8em', marginLeft: '5px' }}>{pendingRequests.length}</span>
        </button>
        <button style={tabStyle('processed')} onClick={() => setActiveTab('processed')}>✅ 처리 내역</button>
        <button style={tabStyle('inventory')} onClick={() => setActiveTab('inventory')}>
          🧪 시약 재고 <span style={{ backgroundColor: '#3498db', color: 'white', borderRadius: '10px', padding: '1px 8px', fontSize: '0.8em', marginLeft: '5px' }}>{reagents.length}</span>
        </button>
        <button style={tabStyle('settings')} onClick={() => setActiveTab('settings')}>⚙️ 설정</button>
      </div>

      {/* ===== 대기중 신청 탭 ===== */}
      {activeTab === 'pending' && (
        <div>
          {Object.keys(pendingBatches).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', color: '#bdc3c7' }}>
              <p style={{ fontSize: '3em', margin: 0 }}>✅</p>
              <p>처리할 신청이 없습니다.</p>
            </div>
          ) : (
            Object.entries(pendingBatches).map(([batchKey, batchItems]) => {
              const { applicant, usageTime, purpose, timestamp } = batchItems[0];
              const submittedAt = timestamp ? new Date(timestamp).toLocaleString('ko-KR') : '';
              return (
                <div key={batchKey} style={{ border: '1px solid #f1c40f', backgroundColor: '#fffdf3', padding: '18px 20px', borderRadius: '12px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <p style={{ margin: '0 0 3px 0', fontWeight: 'bold', fontSize: '1em' }}>👤 {applicant}</p>
                      <p style={{ margin: '0 0 3px 0', fontSize: '0.9em' }}>⏰ <strong>{usageTime}</strong></p>
                      <p style={{ margin: '0 0 3px 0', fontSize: '0.9em' }}>📝 {purpose}</p>
                      {submittedAt && <p style={{ margin: 0, fontSize: '0.78em', color: '#aaa' }}>🕐 신청: {submittedAt}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button
                        onClick={() => handleBatchStatusUpdate(batchItems, '승인')}
                        style={{ padding: '8px 18px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                      >✅ 승인</button>
                      <button
                        onClick={() => handleBatchStatusUpdate(batchItems, '반려')}
                        style={{ padding: '8px 18px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                      >❌ 반려</button>
                    </div>
                  </div>
                  <ul style={{ margin: '12px 0 0 0', paddingLeft: '20px', fontSize: '0.9em', lineHeight: '1.8' }}>
                    {batchItems.map(item => (
                      <li key={item.id}>🧪 <strong>{item.reagentName}</strong>
                        {item.location && item.location !== '위치 미상' && (
                          <span style={{ color: '#999', marginLeft: '6px', fontSize: '0.85em' }}>📍 {item.location}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ===== 처리 내역 탭 ===== */}
      {activeTab === 'processed' && (
        <div>
          {Object.keys(processedBatches).length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '40px' }}>처리된 내역이 없습니다.</p>
          ) : (
            Object.entries(processedBatches).map(([batchKey, batchItems]) => {
              const { applicant, usageTime, purpose, processor } = batchItems[0];
              const statuses = [...new Set(batchItems.map(i => i.status))];
              const batchStatus = statuses.length === 1 ? statuses[0] : '혼합';
              const isApproved = batchStatus === '승인';
              const isRejected = batchStatus === '반려';
              return (
                <div key={batchKey} style={{
                  border: `1px solid ${isApproved ? '#a8e6b8' : isRejected ? '#f5c6c6' : '#ddd'}`,
                  backgroundColor: isApproved ? '#f0fff4' : isRejected ? '#fff5f5' : '#f9f9f9',
                  padding: '15px 20px', borderRadius: '12px', marginBottom: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <p style={{ margin: '0 0 3px 0', fontWeight: 'bold' }}>👤 {applicant}</p>
                      <p style={{ margin: '0 0 3px 0', fontSize: '0.9em' }}>⏰ {usageTime} / 📝 {purpose}</p>
                      {processor && <p style={{ margin: 0, fontSize: '0.8em', color: '#999' }}>처리자: {processor}</p>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <b style={{ color: isApproved ? '#27ae60' : isRejected ? '#c0392b' : '#7f8c8d' }}>
                        {batchStatus}됨
                      </b>
                      <button
                        onClick={() => batchItems.forEach(item => handleDeleteRequest(item.id))}
                        style={{ padding: '4px 10px', backgroundColor: '#ecf0f1', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}
                      >삭제</button>
                    </div>
                  </div>
                  <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: '0.9em', lineHeight: '1.8', color: '#444' }}>
                    {batchItems.map(item => (
                      <li key={item.id}>🧪 <strong>{item.reagentName}</strong>
                        {batchStatus === '혼합' && (
                          <b style={{ fontSize: '0.85em', color: item.status === '승인' ? '#27ae60' : '#c0392b', marginLeft: '8px' }}>
                            {item.status}됨
                          </b>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ===== 시약 재고 탭 ===== */}
      {activeTab === 'inventory' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 시약 이름, 화학식 검색..."
              style={{ padding: '10px', width: '250px', borderRadius: '5px', border: '1px solid #ccc' }}
            />
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {reagents.length === 0 && (
                <button
                  onClick={handleMigrateData}
                  style={{ backgroundColor: '#e67e22', color: 'white', padding: '10px 15px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  🔥 초기 데이터 업로드 ({inventoryData.length}개)
                </button>
              )}
              <button
                onClick={() => openReagentModal()}
                style={{ backgroundColor: '#2980b9', color: 'white', padding: '10px 15px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
              >+ 새 시약 추가</button>
            </div>
          </div>

          {reagents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '2px dashed #dee2e6' }}>
              <p style={{ fontSize: '2em', margin: '0 0 10px 0' }}>📦</p>
              <p style={{ color: '#7f8c8d', fontWeight: 'bold' }}>시약 데이터가 없습니다.</p>
              <p style={{ color: '#aaa', fontSize: '0.9em' }}>위의 <strong>"초기 데이터 업로드"</strong> 버튼을 눌러 JSON 파일의 데이터를 Firebase에 등록하세요.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', backgroundColor: 'white', fontSize: '0.9em' }}>
              <thead>
                <tr style={{ backgroundColor: '#34495e', color: 'white' }}>
                  <th style={{ padding: '10px' }}>한글명</th>
                  <th style={{ padding: '10px' }}>화학식</th>
                  <th style={{ padding: '10px' }}>위치</th>
                  <th style={{ padding: '10px' }}>수량</th>
                  <th style={{ padding: '10px' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredReagents.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px', fontWeight: 'bold' }}>{item.KORNAME || item.TRINAME || item.IUPACNAME}</td>
                    <td style={{ padding: '10px', color: '#e74c3c' }}>{item.MOL_FORMULA}</td>
                    <td style={{ padding: '10px' }}>{item.LOCATION || '-'}</td>
                    <td style={{ padding: '10px' }}>{item.COUNT || '-'}</td>
                    <td style={{ padding: '10px' }}>
                      <button
                        onClick={() => openReagentModal(item)}
                        style={{ marginRight: '5px', padding: '5px 10px', cursor: 'pointer', backgroundColor: '#ecf0f1', border: '1px solid #ccc', borderRadius: '3px' }}
                      >수정</button>
                      <button
                        onClick={() => handleDeleteReagent(item.id)}
                        style={{ padding: '5px 10px', color: 'white', backgroundColor: '#e74c3c', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                      >삭제</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ===== 설정 탭 ===== */}
      {activeTab === 'settings' && (
        <div style={{ padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '15px', border: '1px solid #ddd' }}>
          <h2 style={{ color: '#2c3e50', marginTop: 0 }}>⚙️ 관리자 설정</h2>

          {/* 시약 DB 전체 교체 */}
          <div style={{ marginTop: '20px', padding: '20px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', border: '1px solid #fadbd8' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#c0392b' }}>🔄 시약 DB 전체 교체</h3>
            <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '15px' }}>
              현재 DB의 시약 데이터를 <strong>JSON 파일 데이터 ({inventoryData.length}개)</strong>로 완전히 교체합니다.<br />
              <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>⚠️ 기존 데이터는 모두 삭제됩니다. 신중하게 사용하세요.</span>
            </p>
            <p style={{ color: '#555', fontSize: '0.9em', marginBottom: '15px' }}>
              현재 DB 시약 수: <strong>{reagents.length}개</strong> → 교체 후: <strong>{inventoryData.length}개</strong>
            </p>
            <button
              onClick={handleReplaceAllReagents}
              disabled={isReplacing}
              style={{ padding: '10px 20px', backgroundColor: isReplacing ? '#bdc3c7' : '#c0392b', color: 'white', border: 'none', borderRadius: '5px', cursor: isReplacing ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
            >
              {isReplacing ? '⏳ 교체 중... 잠시 기다려주세요' : `🔄 새 데이터(${inventoryData.length}개)로 전체 교체`}
            </button>
          </div>

          {/* 선생님 가입 비밀번호 관리 */}
          <div style={{ marginTop: '20px', padding: '20px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#e67e22' }}>👩‍🏫 선생님 회원가입 비밀번호 관리</h3>
            <p style={{ color: '#7f8c8d', fontSize: '0.9em', marginBottom: '15px' }}>
              새로운 선생님이 회원가입할 때 입력해야 하는 보안 코드를 변경할 수 있습니다.<br />
              현재 비밀번호: <strong style={{ color: '#c0392b', fontSize: '1.2em', backgroundColor: '#fadbd8', padding: '3px 8px', borderRadius: '5px', marginLeft: '5px' }}>{currentSecretCode}</strong>
            </p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={newSecretCode}
                onChange={(e) => setNewSecretCode(e.target.value)}
                placeholder="새로운 비밀번호 입력"
                style={{ padding: '10px', width: '250px', borderRadius: '5px', border: '1px solid #ccc' }}
              />
              <button
                onClick={handleUpdateSecretCode}
                style={{ padding: '10px 20px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                비밀번호 변경하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 시약 추가/수정 모달 */}
      {isEditModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', width: '460px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>
            <h2 style={{ marginTop: 0, borderBottom: '2px solid #3498db', paddingBottom: '10px', color: '#2c3e50' }}>
              {editingReagent.id ? '📝 시약 상세 정보 수정' : '✨ 새 시약 추가'}
            </h2>
            <div style={{ display: 'grid', gap: '8px', marginTop: '15px' }}>
              {[
                { label: '한글명', key: 'KORNAME', placeholder: '예: 아세트산', required: true },
                { label: '영문명 (IUPAC)', key: 'IUPACNAME', placeholder: '예: Acetic acid' },
                { label: '관용명', key: 'TRINAME', placeholder: '예: 빙초산' },
                { label: '화학식', key: 'MOL_FORMULA', placeholder: '예: CH3COOH', color: '#e74c3c' },
                { label: '몰 질량 (g/mol)', key: 'MOL_MASS', placeholder: '예: 60.05' },
                { label: 'CAS 번호', key: 'CAS_NO', placeholder: '예: 64-19-7' },
                { label: 'NFPA 화재 (R)', key: 'NFPA_R', placeholder: '0~4' },
                { label: 'NFPA 반응성 (Y)', key: 'NFPA_Y', placeholder: '0~4' },
                { label: 'NFPA 건강 (B)', key: 'NFPA_B', placeholder: '0~4' },
                { label: 'NFPA 특수 (W)', key: 'NFPA_W', placeholder: '예: OX, W' },
                { label: '보관 위치', key: 'LOCATION', placeholder: '예: 1-1 상', color: '#d35400' },
                { label: '수량', key: 'COUNT', placeholder: '예: 2' },
              ].map(({ label, key, placeholder, required, color }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <span style={{ minWidth: '130px', fontSize: '0.82em', color: '#7f8c8d', fontWeight: 'bold', flexShrink: 0 }}>
                    {label}{required && <span style={{ color: '#e74c3c' }}> *</span>}
                  </span>
                  <input
                    type="text"
                    value={editingReagent[key] || ''}
                    onChange={(e) => setEditingReagent({ ...editingReagent, [key]: e.target.value })}
                    placeholder={placeholder}
                    style={{ flex: 1, padding: '7px 10px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '0.9em', color: color || '#2c3e50', boxSizing: 'border-box' }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '25px' }}>
              <button onClick={() => setIsEditModalOpen(false)} style={{ padding: '10px 15px', border: 'none', borderRadius: '5px', cursor: 'pointer', backgroundColor: '#ecf0f1' }}>닫기 / 취소</button>
              <button onClick={handleSaveReagent} style={{ padding: '10px 15px', backgroundColor: '#2980b9', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>저장하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherDashboard;